/**
 * Base Form Component
 * Component chính quản lý form state, submit, reset
 */

import {
    Component,
    Input,
    Output,
    EventEmitter,
    OnInit,
    OnChanges,
    OnDestroy,
    SimpleChanges,
    ElementRef,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    ContentChild,
    TemplateRef
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime } from 'rxjs/operators';

import { FormSchema, FormLayout, FormActions, FormOptions } from '../../models/form-schema.interface';
import { FieldConfig } from '../../models/field-config.interface';
import { FormBuilderService } from '../../services/form-builder.service';
import { ValidationService } from '../../services/validation.service';

export interface FormSubmitEvent {
    values: { [key: string]: any };
    isValid: boolean;
    form: FormGroup;
}

export interface FormChangeEvent {
    values: { [key: string]: any };
    changedField?: string;
    isValid: boolean;
    isDirty: boolean;
    form: FormGroup;
}

@Component({
    selector: 'app-base-form',
    templateUrl: './base-form.component.html',
    styleUrls: ['./base-form.component.scss']
})
export class BaseFormComponent implements OnInit, OnChanges, OnDestroy {

    // Required inputs
    @Input() schema!: FormSchema;

    // Optional inputs
    @Input() initialData?: { [key: string]: any };
    @Input() loading: boolean = false;
    @Input() submitLabel: string = 'Lưu';
    @Input() cancelLabel: string = 'Hủy';
    @Input() resetLabel: string = 'Đặt lại';
    @Input() showActions: boolean = true;
    @Input() showReset: boolean = false;
    @Input() showCancel: boolean = true;

    @ContentChild('customField') customFieldTemplate?: TemplateRef<any>;

    // Events
    @Output() formSubmit = new EventEmitter<FormSubmitEvent>();
    @Output() formCancel = new EventEmitter<void>();
    @Output() formReset = new EventEmitter<void>();
    @Output() formChange = new EventEmitter<FormChangeEvent>();
    @Output() formReady = new EventEmitter<FormGroup>();

    // Form instance
    form!: FormGroup;

    // State
    isSubmitting: boolean = false;

    private destroy$ = new Subject<void>();

    constructor(
        private formBuilderService: FormBuilderService,
        private validationService: ValidationService,
        private el: ElementRef,
        private cdr: ChangeDetectorRef
    ) { }

    ngOnInit(): void {
        this.buildForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // Rebuild form khi schema thay đổi
        if (changes['schema'] && !changes['schema'].firstChange) {
            this.buildForm();
        }

        // Update values khi initialData thay đổi
        if (changes['initialData'] && !changes['initialData'].firstChange && this.form) {
            this.formBuilderService.patchFormValues(this.form, this.initialData || {});
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // ===== PUBLIC METHODS =====

    /**
     * Get current form values
     */
    getFormValues(): { [key: string]: any } {
        return this.formBuilderService.getFormValue(this.form);
    }

    /**
     * Check if form is valid
     */
    isFormValid(): boolean {
        return this.form?.valid ?? false;
    }

    /**
     * Check if form is dirty
     */
    isFormDirty(): boolean {
        return this.form?.dirty ?? false;
    }

    /**
     * Patch form values
     */
    patchValues(values: { [key: string]: any }): void {
        this.formBuilderService.patchFormValues(this.form, values);
    }

    /**
     * Reset form
     */
    resetForm(): void {
        this.formBuilderService.resetForm(this.form, this.schema, this.initialData);
        this.formReset.emit();
    }

    /**
     * Mark all fields as touched (để hiển thị errors)
     */
    markAllAsTouched(): void {
        this.validationService.markAllAsTouched(this.form);
    }

    /**
     * Get control by key
     */
    getControl(key: string) {
        return this.form.get(key);
    }

    // ===== GETTERS =====

    get formLayout(): FormLayout {
        return this.schema?.layout || { columns: 1, labelPosition: 'top' };
    }

    get formOptions(): FormOptions {
        return this.schema?.options || {};
    }

    get formActions(): FormActions | undefined {
        return this.schema?.actions;
    }

    get fields(): FieldConfig[] {
        return this.schema?.fields || [];
    }

    get gridClass(): string {
        const columns = this.formLayout.columns || 1;
        return `form-grid columns-${columns}`;
    }

    get actionsPosition(): string {
        return this.formActions?.position || 'right';
    }

    // ===== EVENT HANDLERS =====

    onSubmit(): void {
        // Mark all as touched để hiển thị errors
        this.markAllAsTouched();

        // Focus vào field lỗi đầu tiên nếu form invalid
        if (!this.isFormValid()) {
            this.focusFirstInvalidField();

            if (this.formOptions.scrollToError !== false) {
                this.scrollToFirstError();
            }
            return;
        }

        // Emit submit event
        this.isSubmitting = true;
        this.formSubmit.emit({
            values: this.getFormValues(),
            isValid: true,
            form: this.form
        });
    }

    onCancel(): void {
        this.formCancel.emit();
    }

    onReset(): void {
        this.resetForm();
    }

    // ===== PRIVATE METHODS =====

    private buildForm(): void {
        if (!this.schema) {
            return;
        }

        // Build form từ schema
        this.form = this.formBuilderService.buildForm(this.schema, this.initialData);

        // Setup form change listener
        this.setupFormChangeListener();

        // Emit form ready
        this.formReady.emit(this.form);

        // Trigger change detection for OnPush strategy
        this.cdr.markForCheck();
    }

    private setupFormChangeListener(): void {
        // Lắng nghe thay đổi toàn bộ form
        this.form.valueChanges
            .pipe(
                debounceTime(50), // Giảm debounce để mượt hơn
                takeUntil(this.destroy$)
            )
            .subscribe(values => {
                this.formChange.emit({
                    values,
                    isValid: this.isFormValid(),
                    isDirty: this.isFormDirty(),
                    form: this.form
                });
                this.cdr.markForCheck();
            });

        // Lắng nghe từng control để bắt changedField
        Object.keys(this.form.controls).forEach(key => {
            this.form.get(key)?.valueChanges
                .pipe(takeUntil(this.destroy$))
                .subscribe(value => {
                    this.formChange.emit({
                        values: this.form.value,
                        changedField: key,
                        isValid: this.form.valid,
                        isDirty: this.form.dirty,
                        form: this.form
                    });
                    this.cdr.markForCheck();
                });
        });
    }

    private focusFirstInvalidField(): void {
        for (const field of this.fields) {
            const control = this.form.get(field.key);
            if (control && control.invalid) {
                const inputElement = this.el.nativeElement.querySelector(
                    `[formcontrolname="${field.key}"], [id="${field.key}"]`
                );
                if (inputElement) {
                    inputElement.focus();
                    break;
                }
            }
        }
    }

    private scrollToFirstError(): void {
        const errorElement = this.el.nativeElement.querySelector('.has-error, .ng-invalid.ng-touched');
        if (errorElement) {
            errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }

    /**
     * Track by function for ngFor
     */
    trackByFieldKey(index: number, field: FieldConfig): string {
        return field.key;
    }
}
